import { useState, useRef, useCallback } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronUp, Loader2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { importAppointments, ImportResult } from "@/services/import-service";
import { useClinic } from "@/contexts/ClinicContext";

type UploadState = "idle" | "dragging" | "uploading" | "done";

export default function ImportAppointments() {
  const { selectedClinicId } = useClinic();
  const [uploadState, setUploadState] = useState<UploadState>("idle");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const validateFile = (file: File): boolean => {
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast({
        title: "Formato inválido",
        description: "Apenas arquivos .xlsx ou .xls são aceitos.",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleFileSelect = (file: File) => {
    if (!validateFile(file)) return;
    setSelectedFile(file);
    setResult(null);
    setShowErrors(false);
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setUploadState("dragging");
  }, []);

  const handleDragLeave = useCallback(() => {
    setUploadState("idle");
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setUploadState("idle");
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (!selectedClinicId) {
      toast({ title: "Selecione uma clínica no topo da tela antes de importar.", variant: "destructive" });
      return;
    }
    if (!selectedFile) return;

    setUploadState("uploading");
    setResult(null);

    try {
      const data = await importAppointments(selectedFile, selectedClinicId);
      setResult(data);
      setUploadState("done");

      if (data.erros === 0) {
        toast({
          title: "Importação concluída!",
          description: `${data.novos} novo(s) · ${data.atualizados} atualizado(s) · ${data.ignorados} ignorado(s).`,
        });
      } else {
        toast({
          title: "Importação com avisos",
          description: `${data.novos} novo(s) · ${data.atualizados} atualizado(s) · ${data.erros} erro(s).`,
          variant: "destructive",
        });
      }
    } catch (error) {
      setUploadState("idle");
      toast({
        title: "Falha na importação",
        description: error instanceof Error ? error.message : "Erro desconhecido.",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setResult(null);
    setShowErrors(false);
    setUploadState("idle");
  };

  const [copied, setCopied] = useState(false);

  const handleCopyErrors = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.detalhes_erros.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const allSuccess = result && result.erros === 0;
  const hasErrors = result && result.erros > 0;

  return (
    <div className="page-wrapper max-w-2xl">
      {/* Header */}
      <div>
        <h1 className="page-title">Importar Atendimentos</h1>
        <p className="page-subtitle">
          Envie uma planilha .xlsx para cadastrar atendimentos em lote.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
        className={`
          relative rounded-xl border-2 border-dashed transition-all duration-200 cursor-pointer
          ${uploadState === "dragging"
            ? "border-primary bg-primary/5 scale-[1.01]"
            : selectedFile
            ? "border-primary/40 bg-primary/5 cursor-default"
            : "border-border hover:border-primary/50 hover:bg-muted/30"
          }
        `}
      >
        <div className="flex flex-col items-center justify-center gap-3 p-10 text-center">
          {selectedFile ? (
            <>
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10">
                <FileSpreadsheet className="h-7 w-7 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-foreground">{selectedFile.name}</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground hover:text-destructive text-xs"
                onClick={(e) => { e.stopPropagation(); handleReset(); }}
                disabled={uploadState === "uploading"}
              >
                Remover arquivo
              </Button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted">
                <Upload className="h-7 w-7 text-muted-foreground" />
              </div>
              <div>
                <p className="font-semibold text-foreground">
                  Arraste o arquivo aqui
                </p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  ou clique para selecionar · .xlsx / .xls
                </p>
              </div>
            </>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleInputChange}
        />
      </div>

      {/* Action button */}
      {selectedFile && !result && (
        <Button
          onClick={handleUpload}
          disabled={uploadState === "uploading"}
          className="w-full gap-2"
          size="lg"
        >
          {uploadState === "uploading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Importando...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              Importar Agora
            </>
          )}
        </Button>
      )}

      {/* Result card */}
      {result && (
        <div className={`rounded-xl border p-5 space-y-4 ${
          allSuccess ? "border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800"
                     : "border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800"
        }`}>
          {/* Status icon + title */}
          <div className="flex items-center gap-3">
            {allSuccess ? (
              <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
            ) : (
              <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
            )}
            <div>
              <p className="font-semibold text-foreground">
                {allSuccess ? "Importação concluída!" : "Importação concluída com erros"}
              </p>
              <p className="text-sm text-muted-foreground">{result.arquivo}</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg bg-background/60 border px-4 py-3 text-center">
              <p className="text-2xl font-bold text-foreground">{result.total_processados}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Total</p>
            </div>
            <div className="rounded-lg bg-background/60 border px-4 py-3 text-center">
              <p className="text-2xl font-bold text-green-600">{result.novos}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Novos</p>
            </div>
            <div className="rounded-lg bg-background/60 border px-4 py-3 text-center">
              <p className="text-2xl font-bold text-blue-600">{result.atualizados}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Atualizados</p>
            </div>
            <div className="rounded-lg bg-background/60 border px-4 py-3 text-center">
              <p className="text-2xl font-bold text-slate-400">{result.ignorados}</p>
              <p className="text-xs text-muted-foreground mt-0.5">Ignorados</p>
            </div>
            <div className="rounded-lg bg-background/60 border px-4 py-3 text-center col-span-2 sm:col-span-4">
              <p className={`text-2xl font-bold ${result.erros > 0 ? "text-red-500" : "text-muted-foreground"}`}>
                {result.erros}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">Erros</p>
            </div>
          </div>

          {/* Error details */}
          {hasErrors && (
            <div>
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setShowErrors(!showErrors)}
                  className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:underline"
                >
                  {showErrors ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showErrors ? "Ocultar" : "Ver"} detalhes dos erros
                </button>
                <button
                  onClick={handleCopyErrors}
                  className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400 hover:underline"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? "Copiado!" : "Copiar erros"}
                </button>
              </div>

              {showErrors && (
                <ul className="mt-3 space-y-1.5 max-h-48 overflow-y-auto pr-1">
                  {result.detalhes_erros.map((err, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80">{err}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* New import button */}
          <Button variant="outline" size="sm" onClick={handleReset} className="w-full">
            Nova importação
          </Button>
        </div>
      )}
    </div>
  );
}